import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import RupiahInput from "@/components/RupiahInput";
import api, { formatDetail } from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";

/**
 * 2026-10-19 (user request "KLAIM RETENSI MENUNGGU PEMBAYARAN"): alokasikan
 * satu transaksi penerimaan (receipt) ke klaim retensi. Satu transfer bank
 * boleh membayar beberapa klaim — backend menyimpan alokasi terpisah
 * (receipt_id, claim_id, allocated_amount), menolak Σ alokasi > nilai receipt
 * (409), dan idempotent via idempotency_key. Tidak mengubah histori transaksi.
 */
export default function AllocateReceiptDialog({ open, onOpenChange, claim, onSaved }) {
  const [receipts, setReceipts] = useState([]);
  const [receiptId, setReceiptId] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!claim?.project_id) return;
    api.get(`/active-projects/${claim.project_id}/retention-receipts`)
      .then(({ data }) => setReceipts(data?.receipts || []))
      .catch(() => setReceipts([]));
  };

  useEffect(() => {
    if (open && claim?.project_id) {
      setReceiptId(""); setAmount("");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, claim?.project_id]);

  const candidates = useMemo(() => receipts.filter((r) => r.remainder > 0.005), [receipts]);
  const selected = candidates.find((r) => r.receipt_id === receiptId) || null;
  const maxAmount = selected ? Math.min(selected.remainder, Number(claim?.outstanding) || Infinity) : 0;

  const save = async () => {
    const amt = parseFloat(String(amount || "").replace(/[^0-9.]/g, "")) || 0;
    if (!selected) { toast.error("Pilih receipt sumber dulu"); return; }
    if (amt <= 0) { toast.error("Nominal alokasi harus lebih dari 0"); return; }
    if (amt > selected.remainder + 0.01) { toast.error(`Melebihi sisa receipt (maks ${formatIDR(selected.remainder)})`); return; }
    setBusy(true);
    try {
      await api.post(`/active-projects/${claim.project_id}/income/${claim.entry_id}/allocations`, {
        receipt_id: selected.receipt_id,
        allocations: [{ amount: amt }],
        idempotency_key: `alloc-${claim.entry_id}-${selected.receipt_id}-${amt}-${Date.now()}`,
      });
      toast.success(`Alokasi ${formatIDR(amt)} tercatat ke klaim ini`);
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan alokasi");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="allocate-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Alokasikan Pembayaran ke Klaim Retensi</DialogTitle>
          <DialogDescription className="text-xs">
            Klaim <span className="font-mono">{claim?.invoice_number || claim?.entry_id?.slice(0, 8)}</span> · sisa klaim <b>{formatIDR(claim?.outstanding || 0)}</b>.
            Pilih receipt (bukti penerimaan) di proyek induk + anak — satu receipt boleh membayar beberapa klaim.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {candidates.length === 0 ? (
            <div className="text-xs text-slate-500 italic border border-slate-200 bg-slate-50 px-3 py-3">
              Tidak ada receipt dengan sisa yang dapat dialokasikan di proyek induk + anak.
            </div>
          ) : (
            <div className="border border-slate-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-2 py-2 w-10"></th>
                    <th className="px-3 py-2 text-left">Sumber</th>
                    <th className="px-3 py-2 text-left">Tanggal</th>
                    <th className="px-3 py-2 text-left">Referensi</th>
                    <th className="px-3 py-2 text-right">Nilai</th>
                    <th className="px-3 py-2 text-right">Sisa Bisa Dialokasi</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((r) => (
                    <tr key={r.receipt_id}
                      className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${receiptId === r.receipt_id ? "bg-emerald-50" : ""}`}
                      onClick={() => setReceiptId(r.receipt_id)}
                      data-testid={`alloc-receipt-${r.receipt_id}`}>
                      <td className="px-2 py-2 text-center"><input type="radio" checked={receiptId === r.receipt_id} onChange={() => setReceiptId(r.receipt_id)} /></td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-800">{r.project_name}</div>
                        <div className="text-[10px] text-slate-500">{r.host_is_claim ? "Klaim retensi" : "Invoice"} · {r.entry_label}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.date ? fmtDate(r.date) : "—"}</td>
                      <td className="px-3 py-2 text-slate-700">{[r.bank_name, r.account_no, r.transfer_ref].filter(Boolean).join(" · ") || <span className="text-rose-600 italic">Belum lengkap</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatIDR(r.amount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{formatIDR(r.remainder)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {selected && (
            <div className="space-y-1.5" data-testid="alloc-amount-wrapper">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Nominal Alokasi (maks {formatIDR(maxAmount)})
              </div>
              <div className="flex items-center gap-2">
                <RupiahInput testid="alloc-amount" value={amount} onChange={setAmount} className="w-56" placeholder="0" />
                <button type="button" data-testid="alloc-fill-max"
                  onClick={() => setAmount(String(Math.floor(maxAmount)))}
                  className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider border border-slate-300 hover:bg-slate-100">
                  Isi Maks
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="alloc-cancel">Batal</Button>
            <Button type="button" onClick={save} disabled={busy || !selected} data-testid="alloc-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? "Menyimpan…" : "Simpan Alokasi"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
